import { useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { LEVEL_LABELS, ORDERED_LEVELS, MAX_KRS_PER_OBJECTIVE } from "@itspip/shared";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormError } from "@/components/shared/form-error";
import { useObjectives } from "@/hooks/use-objectives";
import { useMoveKeyResult } from "@/hooks/use-key-results";
import { isTerminalStatus } from "@/lib/status-machine";
import type { KeyResult, Objective, ObjectiveLevel } from "@/types/entities";
import { toast } from "sonner";

interface MoveKeyResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keyResult: KeyResult;
  sourceObjective: Objective | null | undefined;
}

interface DestOption {
  id: string;
  label: string;
  year: number;
  full: boolean;
}

interface DestGroup {
  level: ObjectiveLevel;
  label: string;
  items: DestOption[];
}

function buildDestGroups(
  objectives: Objective[],
  excludeObjectiveId: string,
): DestGroup[] {
  const groups: DestGroup[] = [];
  for (const lvl of ORDERED_LEVELS) {
    const items: DestOption[] = [];
    for (const o of objectives) {
      if (o.level !== lvl) continue;
      if (o.id === excludeObjectiveId) continue;
      if (isTerminalStatus(o.status)) continue;
      const krTotal = o.kr_total ?? 0;
      const full = krTotal >= MAX_KRS_PER_OBJECTIVE;
      const ownerSuffix = o.owner_name ? ` — ${o.owner_name}` : "";
      const fullSuffix = full ? " (full)" : "";
      items.push({
        id: o.id,
        label: `${o.title}${ownerSuffix}${fullSuffix}`,
        year: o.year,
        full,
      });
    }
    if (items.length > 0) {
      items.sort((a, b) => a.label.localeCompare(b.label));
      groups.push({ level: lvl, label: LEVEL_LABELS[lvl], items });
    }
  }
  return groups;
}

export function MoveKeyResultDialog({
  open,
  onOpenChange,
  keyResult,
  sourceObjective,
}: MoveKeyResultDialogProps) {
  const { data: objectivesData, isLoading: objectivesLoading } = useObjectives({});
  const moveKeyResult = useMoveKeyResult(keyResult.id);
  const [selectedId, setSelectedId] = useState<string>("");
  const [serverError, setServerError] = useState<string | null>(null);

  const groups = useMemo(
    () => buildDestGroups(objectivesData?.data ?? [], keyResult.objective_id),
    [objectivesData, keyResult.objective_id],
  );

  const allOptions = useMemo(
    () => groups.flatMap((g) => g.items),
    [groups],
  );
  const selectedOption = allOptions.find((o) => o.id === selectedId) ?? null;

  const yearMismatch =
    selectedOption != null &&
    sourceObjective != null &&
    selectedOption.year !== sourceObjective.year;

  const handleClose = (next: boolean) => {
    if (!next) {
      setSelectedId("");
      setServerError(null);
    }
    onOpenChange(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    if (!selectedId) {
      setServerError("Select a destination objective");
      return;
    }
    if (selectedOption?.full) {
      setServerError(
        `Destination already has the maximum of ${MAX_KRS_PER_OBJECTIVE} key results`,
      );
      return;
    }
    moveKeyResult.mutate(selectedId, {
      onSuccess: () => {
        toast.success(`Moved to "${selectedOption?.label.split(" — ")[0] ?? "objective"}"`);
        handleClose(false);
      },
      onError: (e) => {
        setServerError(e.message);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Move Key Result</DialogTitle>
          <DialogDescription>
            Reassign this key result to a different objective. All check-ins,
            progress updates, kudos, and contributors stay attached.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="move-kr-destination">Destination objective</Label>
            <Select
              value={selectedId}
              onValueChange={(v) => {
                setSelectedId(v);
                setServerError(null);
              }}
              disabled={objectivesLoading}
            >
              <SelectTrigger id="move-kr-destination">
                <SelectValue
                  placeholder={objectivesLoading ? "Loading..." : "Select an objective"}
                />
              </SelectTrigger>
              <SelectContent>
                {groups.length === 0 && !objectivesLoading && (
                  <div className="px-2 py-1.5 text-sm text-[hsl(var(--muted-foreground))]">
                    No eligible objectives
                  </div>
                )}
                {groups.map((group) => (
                  <SelectGroup key={group.level}>
                    <SelectLabel>{group.label}</SelectLabel>
                    {group.items.map((item) => (
                      <SelectItem key={item.id} value={item.id} disabled={item.full}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          {yearMismatch && sourceObjective && selectedOption && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <div>
                Destination is in <strong>{selectedOption.year}</strong>; source is in{" "}
                <strong>{sourceObjective.year}</strong>. Quarter{" "}
                <strong>{keyResult.quarter}</strong> will be reinterpreted against{" "}
                {selectedOption.year}.
              </div>
            </div>
          )}

          <FormError message={serverError ?? undefined} />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={moveKeyResult.isPending || !selectedId}>
              {moveKeyResult.isPending ? "Moving..." : "Move"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
